import { INVOICE_ACTION_BUTTON_LABELS, INVOICE_ACTIONS, APP_VERSION } from "const";
import * as TerminalHelper from "helpers/TermialHelper";
import { roundOff, customerAmountFormat, toWords } from "helpers/NumberFormatHelper";
import { getInvoiceFormat } from "helpers/InvoiceHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";

export function getInvoiceActionButtonLabel() {
  const terminalSettings = TerminalHelper.getTerminalSettings();
  const action = terminalSettings.defaultInvoiceAction || INVOICE_ACTIONS.PRINT;
  return INVOICE_ACTION_BUTTON_LABELS[action] || INVOICE_ACTION_BUTTON_LABELS[INVOICE_ACTIONS.PRINT];
}

function buildWhatsAppHeaderAndMeta(sale, shop, invoiceFormat) {
  const { customer = {} } = sale;
  const shopName = invoiceFormat?.shop_name || shop.name || "";
  const address1 = invoiceFormat?.address_1 || "";
  const address2 = invoiceFormat?.address_2 || "";
  const phone = invoiceFormat?.tel_no || shop.phone || "";

  let message = `*${shopName.toUpperCase()}*\n`;
  if (address1) message += `${address1}\n`;
  if (address2) message += `${address2}\n`;
  if (phone) message += `${phone}\n`;
  message += `\n`;

  const title = sale?.estimate_no ? "ESTIMATE" : invoiceFormat?.header || "TAX INVOICE";
  message += `*${title}*\n\n`;

  const formattedDateTime = `${DateTimeHelpers.getDate(sale.time)} ${DateTimeHelpers.getLocalTime(sale.time)}`;
  const invoiceLabel = sale?.estimate_no ? "Est No" : "Inv No";
  message += `${invoiceLabel} : ${sale?.bill_no || "N/A"}\n`;
  message += `Date : ${formattedDateTime}\n`;

  if (sale?.waiter?.name) {
    message += `Sales Staff : ${sale.waiter.name}\n`;
  }
  if (sale?.order_type) {
    message += `Order Type : ${sale.order_type}\n`;
  }

  if (customer && customer.name) {
    message += `${customer.name}\n`;
    if (customer.mobile) {
      message += `${customer.mobile}\n`;
    }
    if (customer.trn_number) {
      message += `GSTIN : ${customer.trn_number}\n`;
    }
    if (customer.location) {
      message += `${customer.location}\n`;
    }
  }

  return message;
}

function buildWhatsAppProductsList(items, sale, shop) {
  const dashes = "----------------------------------";
  let text = `${dashes}\n*Products*\n${dashes}\n`;

  items.forEach((it, index) => {
    const name = it.name || it.item?.name || "";
    const qty = it.qty || it.quantity || 1;
    const rate = roundOff(it.payable || 0);
    text += `${index + 1}. *${name.toUpperCase()} \t\t${qty}Pcs\t\t${rate}*\n\n`;
  });

  text += `${dashes}\n`;
  const payable = sale?.payable;
  text += `*${items.length}* Items,\t\t Amount: *${roundOff(payable)}*\n`;
  text += `${dashes}\n`;

  const discount = sale.discount || 0;
  if (discount > 0) {
    text += `Total Discount:\t *${roundOff(discount)}*\n`;
  }
  text += `Total before tax :\t *${roundOff(sale?.total)}*\n`;

  const tax = Number(sale.tax || 0);
  if (tax > 0) {
    if (shop?.tax_type === "GST") {
      const halfTax = tax / 2;
      text += `CGST: \t*${roundOff(halfTax)}*\n`;
      text += `SGST: \t*${roundOff(halfTax)}*\n`;
    } else {
      text += `Tax: \t*${roundOff(tax)}*\n`;
    }
  }

  const cess = Number(sale.cess || 0);
  if (cess > 0) {
    text += `CESS: \t*${roundOff(cess)}*\n`;
  }

  return text;
}

function buildWhatsAppPaymentBlock(sale, payable) {
  const dashes = "----------------------------------";
  const terminalSettings = TerminalHelper.getTerminalSettings();
  const paymentModeOnInvoice = !!terminalSettings?.paymentModeOnInvoice;

  let hasPaymentsOrBalance = false;
  let paymentBlock = "";

  if (paymentModeOnInvoice && (sale.payment_modes || []).length > 0) {
    sale.payment_modes.forEach((pm) => {
      paymentBlock += `*${(pm.name || "Payment").toUpperCase()} \t\t\t ${roundOff(pm.amount || 0)}*\n`;
      hasPaymentsOrBalance = true;
    });
  }

  if (Number(sale.customer_credit_used || 0) !== 0) {
    paymentBlock += `*CUSTOMER CREDIT \t\t\t ${roundOff(sale.customer_credit_used)}*\n`;
    hasPaymentsOrBalance = true;
  }

  const totalTender = (sale.payment_modes || []).reduce((sum, pm) => sum + Number(pm.amount || 0), 0) || payable;
  paymentBlock += `*TOTAL TENDER: ${roundOff(totalTender)}*\n`;
  hasPaymentsOrBalance = true;

  if (sale.customer && sale.customer.balance !== undefined) {
    const prevBalance = Number(sale.customer.balance || 0) + Number(sale.customer_credit_used || 0);
    paymentBlock += `*Prev. BALANCE:${customerAmountFormat(prevBalance)}*\n`;
    paymentBlock += `*CUSTOMER BALANCE:${customerAmountFormat(sale.customer.balance)}*\n`;
    hasPaymentsOrBalance = true;
  }

  let text = "";
  if (hasPaymentsOrBalance) {
    text += `${dashes}\n${paymentBlock}`;
  }
  return text;
}

export function generateWhatsAppMessage(sale = {}, shop = {}) {
  const { items = [], payable = 0 } = sale;
  const invoiceFormat = getInvoiceFormat() || {};

  let message = buildWhatsAppHeaderAndMeta(sale, shop, invoiceFormat);
  message += buildWhatsAppProductsList(items, sale, shop);
  message += `*SUB TOTAL \t\t ${roundOff(payable)}*\n`;
  message += buildWhatsAppPaymentBlock(sale, payable);
  message += `\nIn words: ${toWords(payable)}\n\n`;

  if (invoiceFormat?.footer_1) message += `${invoiceFormat.footer_1}\n`;
  if (invoiceFormat?.footer_2) message += `${invoiceFormat.footer_2}\n`;
  message += `v${APP_VERSION}`;

  return encodeURIComponent(message);
}

export function shareInvoiceOnWhatsApp(sale, shop) {
  if (!sale?.customer || !sale?.customer.mobile) {
    throw new Error("No customer selected or missing mobile number.");
  }
  const message = generateWhatsAppMessage(sale, shop);
  const mobile = (sale.customer.mobile || "").replace(/\D/g, "");
  const whatsappUrl = `https://wa.me/+91${mobile}?text=${message}`;
  window.open(whatsappUrl, "_blank");
}

function formatPaymentDateTime(time) {
  const dateObj = DateTimeHelpers.parseLocalISO(time);

  const pad = (num) => String(num).padStart(2, "0");
  const d = pad(dateObj.getDate());
  const m = pad(dateObj.getMonth() + 1);
  const y = dateObj.getFullYear();
  let hours = dateObj.getHours();
  const minutes = pad(dateObj.getMinutes());
  const ampmStr = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${d}-${m}-${y} ${pad(hours)}:${minutes} ${ampmStr}`;
}

function buildPaymentCustomerInfo(customer, note) {
  let text = "";
  if (customer?.name) {
    text += `Customer : ${customer.name}\n`;
    if (customer.mobile) {
      text += `Mobile : ${customer.mobile}\n`;
    }
  }
  const dashes = "----------------------------------";
  text += `${dashes}\nPayment Description : ${note || "Payment received"}\n${dashes}\n`;
  return text;
}

function buildPaymentTendersAndTotals(payment) {
  const { tenders = [], amount = 0, customer = {} } = payment;
  let text = "";
  if (tenders.length > 0) {
    tenders.forEach((t) => {
      text += `*${(t.payment_mode?.name || "Payment").toUpperCase()} : ${roundOff(t.amount)}*\n`;
    });
  }
  const paidVal = amount || payment.credit || 0;
  text += `*PAID : ${roundOff(paidVal)}*\n`;
  if (customer.balance !== undefined) {
    text += `*CUSTOMER BALANCE : ${customerAmountFormat(customer.balance)}*\n`;
  }
  text += `\nIn words: ${toWords(paidVal)} only\n\n`;
  return text;
}

export function generateCustomerPaymentWhatsAppMessage(payment = {}, shop = {}) {
  const { customer = {}, note = "", time } = payment;
  const fmt = getInvoiceFormat() || {};
  const shopName = fmt?.shop_name || shop.name || "";

  let msg = `*${shopName.toUpperCase()}*\n`;
  if (fmt?.address_1) msg += `${fmt.address_1}\n`;
  if (fmt?.address_2) msg += `${fmt.address_2}\n`;
  if (fmt?.tel_no || shop.phone) msg += `${fmt.tel_no || shop.phone}\n`;
  msg += `\n*PAYMENT RECEIPT*\n\nDate : ${formatPaymentDateTime(time)}\n`;
  msg += buildPaymentCustomerInfo(customer, note);
  msg += buildPaymentTendersAndTotals(payment);
  if (fmt?.footer_1) msg += `${fmt.footer_1}\n`;
  if (fmt?.footer_2) msg += `${fmt.footer_2}\n`;

  return encodeURIComponent(msg);
}

export function shareCustomerPaymentOnWhatsApp(payment, shop, customer) {
  const cust = customer || payment?.customer;
  if (!cust || !cust.mobile) {
    throw new Error("No customer selected or missing mobile number.");
  }
  const message = generateCustomerPaymentWhatsAppMessage(payment, shop);
  const mobile = (cust.mobile || "").replace(/\D/g, "");
  const whatsappUrl = `https://wa.me/+91${mobile}?text=${message}`;
  window.open(whatsappUrl, "_blank");
}

export function generateCustomerJournalWhatsAppMessage(customer = {}, journals = [], shop = {}) {
  const fmt = getInvoiceFormat() || {};
  const shopName = fmt?.shop_name || shop.name || "";

  let message = `*${shopName.toUpperCase()}*\n`;
  if (fmt?.address_1) message += `${fmt.address_1}\n`;
  if (fmt?.address_2) message += `${fmt.address_2}\n`;
  if (fmt?.tel_no || shop.phone) message += `${fmt.tel_no || shop.phone}\n`;
  message += `\n*CUSTOMER STATEMENT*\n\n`;

  message += `Customer : *${customer.name || "-"}*\n`;
  if (customer.mobile) message += `Mobile : ${customer.mobile}\n`;
  if (customer.location) message += `Location : ${customer.location}\n`;
  message += `Balance : *${customerAmountFormat(customer.balance || 0)}*\n`;
  if (customer.credit_limit) {
    message += `Credit Limit : ${customerAmountFormat(customer.credit_limit || 0)}\n`;
  }
  message += `\n`;

  const dashes = "----------------------------------";
  message += `${dashes}\n*TRANSACTIONS*\n${dashes}\n`;

  (journals || []).forEach((item, index) => {
    const dateStr = item.time ? DateTimeHelpers.convertDateToUtcDmy(new Date(item.time)) : "-";
    const match = item.description?.match(/"([^"]+)"/);
    const desc = match ? match[1] : item.description || "-";
    const cr = item.credit ? roundOff(item.credit) : "0";
    const dr = item.debit ? roundOff(item.debit) : "0";
    const bal = customerAmountFormat(item.balance || 0);

    message += `${index + 1}. *${dateStr}* - ${desc}\n`;
    message += `   Cr: ${cr} | Dr: ${dr} | Bal: ${bal}\n\n`;
  });

  if (fmt?.footer_1) message += `${fmt.footer_1}\n`;
  if (fmt?.footer_2) message += `${fmt.footer_2}\n`;

  return encodeURIComponent(message);
}

export function shareCustomerJournalOnWhatsApp(customer = {}, journals = [], shop = {}) {
  if (!customer || !customer.mobile) {
    throw new Error("No customer selected or missing mobile number.");
  }
  const message = generateCustomerJournalWhatsAppMessage(customer, journals, shop);
  const mobile = String(customer.mobile || "").replace(/\D/g, "");
  const whatsappUrl = `https://wa.me/+91${mobile}?text=${message}`;
  window.open(whatsappUrl, "_blank");
}

export function validatePaymentQr(due) {
  const terminalSettings = TerminalHelper.getTerminalSettings();
  if (due > 0 && !!terminalSettings.orderFullPayment) {
    return { valid: false, error: "Tender is less than the bill total." };
  }
  return { valid: true };
}

export function getUpdatedPaymentModesWithQr(paymentModes, finalUpiAmount, upiModes = []) {
  let upiFound = false;
  let updatedModes = paymentModes.map((m) => {
    const isUpiMode = upiModes.some((upiMode) => upiMode.toLowerCase() === m.name.toLowerCase());
    if (isUpiMode && !upiFound) {
      upiFound = true;
      return { ...m, amount: finalUpiAmount };
    }
    return m;
  });

  if (!upiFound && updatedModes.length > 0) {
    updatedModes = updatedModes.map((m, idx) => (idx === 0 ? { ...m, amount: finalUpiAmount } : m));
  }
  return updatedModes;
}
